/**
 * Modal-presentation hook for storefront plugins.
 *
 * The default bundle modal (used in admin-iframe preview) is portal-
 * rendered with bundle-controlled chrome: backdrop, panel, close button,
 * ESC + click-outside dismiss. That's fine for the admin context where
 * we own the styling — but in a merchant's shop the configurator should
 * adopt the host's modal system (Magento `Magento_Ui/js/modal/modal`,
 * Shopware `bootstrap.modal`, Woo `wp.dialog`, …) so close buttons,
 * spacing and z-index conventions match the rest of the storefront.
 *
 * The adapter is intentionally imperative: shop plugins live in
 * RequireJS + jQuery (Magento) or PHP-rendered HTML + vanilla JS
 * (Woo / Shopware), not React. The bundle passes them a populated DOM
 * container; the adapter is responsible for displaying it.
 *
 * Plugins install the adapter on `window.Configento.setModalAdapter`
 * before the bundle finishes loading (`<script src="…configento-v3/
 * configento.js" defer>` runs AFTER the host's inline-script
 * registers the adapter, so the bundle is guaranteed to see it on
 * the first OverlayShell open).
 */
export interface ModalAdapter {
    /**
     * Open a full-screen overlay modal. The bundle hands you an empty
     * `container` element that it will populate via React after this
     * call returns; you mount that container into the DOM (typically
     * inside your host modal's content slot), display the modal, and
     * arrange for `onClose` to be invoked when the user dismisses via
     * any UI path (X button, ESC, backdrop click).
     *
     * Return value's `close()` is called by the bundle when the modal
     * should programmatically close — e.g. user picks an option in a
     * single-pick listimage and the overlay should snap shut.
     */
    openOverlay(args: {
        title: string;
        container: HTMLElement;
        onClose: () => void;
    }): { close(): void };
}

let currentAdapter: ModalAdapter | undefined;

/**
 * Register / replace the active modal adapter. Pass `undefined` (or no
 * arg) to clear and fall back to the bundle's own portal modal.
 *
 * Idempotent within an instance; safe to call multiple times.
 */
export function setModalAdapter(adapter?: ModalAdapter): void {
    currentAdapter = adapter;
}

export function getModalAdapter(): ModalAdapter | undefined {
    return currentAdapter;
}
